/**
 * XML Parser utility for HPRA drug data
 * Works in both browser and Node.js environments
 */

export interface HPRAProduct {
  drugIdPK: string
  licenceNumber: string
  interchangeableListCode?: string
  productName: string
  paHolder: string
  authorisedDate: string
  productType: string
  marketInfo: string
  registrationStatus: string
  dosageForm: string
  atcs: string[]
  legalBasis?: string
  routesOfAdministration?: string[]
  activeSubstances: string[]
  dispensingLegalStatus: string
  supplyLegalStatus: string
  promotionLegalStatus: string
  supplyComments?: string
}

/**
 * Parse HPRA product from XML element (browser DOMParser)
 * Note: Uses getElementsByTagName to handle XML namespaces
 */
function parseProductFromElement(element: Element): HPRAProduct {
  // Use getElementsByTagName to handle namespaces (works regardless of namespace)
  const getText = (tagName: string): string => {
    const els = element.getElementsByTagName(tagName)
    if (els.length > 0) {
      return els[0].textContent?.trim() || ''
    }
    return ''
  }

  // Handle ATC codes - nested in ATCs > ATC
  const atcs: string[] = []
  const atcsEl = element.getElementsByTagName('ATCs')[0]
  if (atcsEl) {
    const atcElements = atcsEl.getElementsByTagName('ATC')
    atcs.push(...Array.from(atcElements).map((el) => el.textContent?.trim() || '').filter(Boolean))
  }

  // Handle routes of administration - nested in RoutesOfAdministration > RoutesOfAdministration
  const routesOfAdministration: string[] = []
  const routesEl = element.getElementsByTagName('RoutesOfAdministration')[0]
  if (routesEl) {
    const routeElements = routesEl.getElementsByTagName('RoutesOfAdministration')
    routesOfAdministration.push(...Array.from(routeElements).map((el) => el.textContent?.trim() || '').filter(Boolean))
  }

  // Handle active substances - nested in ActiveSubstances > ActiveSubstance
  const activeSubstances: string[] = []
  const substancesEl = element.getElementsByTagName('ActiveSubstances')[0]
  if (substancesEl) {
    const substanceElements = substancesEl.getElementsByTagName('ActiveSubstance')
    activeSubstances.push(...Array.from(substanceElements).map((el) => el.textContent?.trim() || '').filter(Boolean))
  }

  // Handle DispensingLegalStatus - nested Status element
  let dispensingLegalStatus = ''
  const dispensingEl = element.getElementsByTagName('DispensingLegalStatus')[0]
  if (dispensingEl) {
    const statusEl = dispensingEl.getElementsByTagName('Status')[0]
    dispensingLegalStatus = statusEl?.textContent?.trim() || dispensingEl.textContent?.trim() || ''
  }

  return {
    drugIdPK: getText('DrugIDPK'),
    licenceNumber: getText('LicenceNumber'),
    interchangeableListCode: getText('InterchangeableListCode') || undefined,
    productName: getText('ProductName'),
    paHolder: getText('PAHolder'),
    authorisedDate: getText('AuthorisedDate'),
    productType: getText('ProductType'),
    marketInfo: getText('MarketInfo'),
    registrationStatus: getText('RegistrationStatus'),
    dosageForm: getText('DosageForm'),
    atcs,
    legalBasis: getText('LegalBasis') || undefined,
    routesOfAdministration: routesOfAdministration.length > 0 ? routesOfAdministration : undefined,
    activeSubstances,
    dispensingLegalStatus,
    supplyLegalStatus: getText('SupplyLegalStatus'),
    promotionLegalStatus: getText('PromotionLegalStatus'),
    supplyComments: getText('SupplyComments') || undefined,
  }
}

/**
 * Parse HPRA XML string (browser environment)
 */
export function parseHPRAXML(xmlString: string): HPRAProduct[] {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml')

  // Check for parsing errors
  const parseError = xmlDoc.querySelector('parsererror')
  if (parseError) {
    const errorText = parseError.textContent || 'Unknown parsing error'
    console.error('XML parsing error:', errorText)
    throw new Error(`XML parsing error: ${errorText}`)
  }

  const products: HPRAProduct[] = []
  
  // Use getElementsByTagName to handle XML namespaces
  // First try to find Products container, then Product children
  let productElements: HTMLCollectionOf<Element> | NodeListOf<Element>
  const productsEl = xmlDoc.getElementsByTagName('Products')[0]
  if (productsEl) {
    productElements = productsEl.getElementsByTagName('Product')
  } else {
    // Fallback: try direct Product elements
    productElements = xmlDoc.getElementsByTagName('Product')
  }

  // Convert HTMLCollectionOf to Array (HTMLCollectionOf doesn't have forEach)
  const productArray = Array.from(productElements)

  console.log(`Found ${productArray.length} product elements in XML`)

  productArray.forEach((element, index) => {
    try {
      const product = parseProductFromElement(element as Element)
      if (product.drugIdPK && product.productName) {
        products.push(product)
      } else {
        if (index < 5) {
          console.warn(`Skipping product ${index}: missing drugIdPK or productName`, {
            drugIdPK: product.drugIdPK,
            productName: product.productName,
          })
        }
      }
    } catch (error) {
      console.warn(`Error parsing product ${index}:`, error)
    }
  })

  console.log(`Successfully parsed ${products.length} products from XML`)
  return products
}

/**
 * Fetch and parse HPRA XML file from URL
 */
export async function fetchAndParseHPRAXML(url: string): Promise<HPRAProduct[]> {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch XML: ${response.status} ${response.statusText}`)
    }
    const xmlString = await response.text()
    return parseHPRAXML(xmlString)
  } catch (error) {
    console.error(`Error fetching/parsing XML from ${url}:`, error)
    throw error
  }
}

